import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthState } from '@/types';
import { api } from '@/lib/api';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =============================================================================
// TOKEN STORAGE: Currently uses localStorage for simplicity
//
// SECURITY NOTE: localStorage is accessible to any JavaScript on the page,
// making it vulnerable to XSS attacks. For enhanced security, consider migrating
// to HttpOnly cookies in the future.
//
// FUTURE MIGRATION TO HttpOnly COOKIES:
// 1. Backend changes needed:
//    - Update login endpoint to set HttpOnly, Secure, SameSite=Strict cookie
//    - Update /api/auth/me to read token from cookie instead of header
//    - Add CSRF protection (double-submit cookie or sync token pattern)
//    - Set cookie path to '/api' to limit exposure
//
// 2. Frontend changes needed:
//    - Remove localStorage token storage below
//    - Remove Authorization header logic in api.ts
//    - Add credentials: 'include' to all fetch requests
//    - Handle CSRF token in requests if implementing CSRF protection
//
// 3. Files to modify:
//    - backend/src/routes/auth.ts (login endpoint cookie setting)
//    - backend/src/middleware/auth.ts (token extraction from cookie)
//    - frontend/src/lib/api.ts (remove token header, add credentials)
//    - frontend/src/hooks/useAuth.tsx (remove localStorage logic)
//
// Current approach is acceptable for low-traffic internal portal with
// proper Content-Security-Policy headers preventing inline scripts.
// =============================================================================

const TOKEN_KEY = 'reseller_portal_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    token: localStorage.getItem(TOKEN_KEY),
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        api.setToken(token);
        try {
          const { user } = await api.getMe();
          setState({
            token,
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          api.setToken(null);
          setState({
            token: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      } else {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const { token, user } = await api.login(email, password);
    localStorage.setItem(TOKEN_KEY, token);
    api.setToken(token);
    setState({
      token,
      user,
      isAuthenticated: true,
      isLoading: false,
    });
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    api.setToken(null);
    setState({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
